/**
 * Actycrea: marquee.esm.js (ES6)
 * MIT
 */

import { PREFIX } from '../helpers/config.js';
import { onResizeEnd } from '../helpers/events.js';

const SELECTOR      = `data-${PREFIX}-marquee`;
const SELECTOR_DATA = `[${SELECTOR}]`;
const SELECTOR_LINE = `[data-${PREFIX}-line]`;

const DEFAULTS = {
  speed: 80,
};

const now = ( ) => ( performance || Date ).now( );

export class MyMQ {
  constructor( element , config = { } ){
    if( !( element instanceof HTMLElement ) ) throw new Error( 'MyMQ: element inválido' );

    this._element = element;

    const dsSpeed = element.dataset.speed;

    this._config = {
      ...DEFAULTS,
      ...config,
      ...( dsSpeed !== undefined && dsSpeed !== '' ? { speed: Number( dsSpeed ) } : { } ),
    };

    if( !Number.isFinite( this._config.speed ) || this._config.speed <= 0 ){
      this._config.speed = DEFAULTS.speed;
    }

    console.log( 'MyMQ: => constructor => this._config ' , this._config );

    this._lines   = Array.from( this._element.querySelectorAll( SELECTOR_LINE ) ).map( ( el , i ) => {
      const attr = String( el.getAttribute( 'data-dir' ) || '' ).toLowerCase( );
      const dir  = attr === 'rtl' ? -1 : attr === 'ltr' ? +1 : ( i % 2 === 0 ? -1 : +1 );
      return { el , dir , offset: 0 };
    } );

    this._reduced = window.matchMedia && window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;
    this._loop        = this._loop.bind( this );
    this._resizeEnd   = this._resizeEnd.bind( this );
    this._running     = false;
    this._last        = now( );

    this._setup( );
  }

  _setup( ){
    this._lines.forEach( l => this._layoutLine( l ) );
    window.addEventListener( onResizeEnd , this._resizeEnd , { passive: true } );
    if( !this._reduced ){
      this._running = true;
      this._last = now( );
      requestAnimationFrame( this._loop );
    }
  }

  _resizeEnd( ){
    this._lines.forEach( l => this._layoutLine( l ) );
  }

  _lineWidth( line ){
    return Array.from( line.el.children )
      .reduce( ( acc , n ) => acc + n.getBoundingClientRect( ).width , 0 );
  }

  _layoutLine( line ){
    // restaurar originales la primera vez
    const originals = Array.from( line.el.querySelectorAll( '[data-original="1"]' ) );
    if( originals.length ){
      line.el.innerHTML = '';
      originals.forEach( n => line.el.appendChild( n ) );
    } else {
      Array.from( line.el.children ).forEach( n => n.setAttribute( 'data-original' , '1' ) );
    }

    // duplicar hasta cubrir al menos 2× contenedor
    const contW = this._element.getBoundingClientRect( ).width || this._element.clientWidth;
    let totalW  = this._lineWidth( line );
    const target = Math.max( contW * 2 , contW + totalW );

    while( totalW < target && line.el.children.length ){
      const current = Array.from( line.el.children );
      for( let i = 0 ; i < current.length && totalW < target ; i++ ){
        const clone = current[ i ].cloneNode( true );
        line.el.appendChild( clone );
        totalW += clone.getBoundingClientRect( ).width;
      }
    }

    // estado inicial
    line.offset = -1; // arranca ligeramente negativo para evitar primer reciclado inmediato a derechas
    line.el.style.transform = `translate3d(${line.offset}px,0,0)`;
  }

  _recycle( line ){
    // Reciclado sin rects absolutos: solo offset y anchos actuales
    if( line.dir < 0 ){
      // izquierda: cuando offset ≤ -ancho del primero, mover primero al final y sumar su ancho
      let first = line.el.firstElementChild;
      while( first ){
        const w = first.getBoundingClientRect( ).width;
        if( line.offset <= -w ){
          line.el.appendChild( first );
          line.offset += w; // compensar para continuidad
          first = line.el.firstElementChild;
        } else { break; }
      }
    } else {
      // derecha: si offset ≥ 0, mover último al inicio y restar su ancho
      let last = line.el.lastElementChild;
      while( last && line.offset >= 0 ){
        const w = last.getBoundingClientRect( ).width;
        line.el.insertBefore( last , line.el.firstElementChild );
        line.offset -= w; // mantener banda continua por la izquierda
        last = line.el.lastElementChild;
      }
    }
  }

  _loop( ){
    if( !this._running ){ return; }
    const t  = now( );
    const dt = Math.min( 0.05 , Math.max( 0 , ( t - this._last ) / 1000 ) );
    this._last = t;

    this._lines.forEach( line => {
      line.offset += line.dir * this._config.speed * dt;
      this._recycle( line );
      // evitar acumulación de coma flotante
      const x = Math.round( line.offset * 1000 ) / 1000;
      line.el.style.transform = `translate3d(${x}px,0,0)`;
    } );

    requestAnimationFrame( this._loop );
  }

  stop( ){ this._running = false; }

  /* ============
   * Init masiva
   * ===========*/
  static initAll( root = document ){
    const nodes = root.querySelectorAll( SELECTOR_DATA );
    const instances = [ ];
    nodes.forEach( el => {
      if( el._mymarqueeInstance ) return;
      let cfg = { };
      const raw = el.getAttribute( SELECTOR );
      if( raw ){
        try { cfg = JSON.parse( raw ); } catch( e ){ cfg = { }; }
      }
      el._mymarqueeInstance = new MyMQ( el , cfg );
      instances.push( el._mymarqueeInstance );
    } );
    return instances;
  }
}

export default MyMQ;
